// Package imports:
import React from 'react';
// Component imports:
import Flag, { FlagNames } from '../../../ui-elements/Flag/Flag';
import ColoredNumber from '../../../ui-elements/ColoredNumber/ColoredNumber';
import Alert from '../../../ui-elements/Alert/Alert';
import Loading from '../../../ui-elements/Loading/Loading';
import DisplayBox from '../../DisplayBox/DisplayBox';
import IntradayChart from '../IntradayChart/IntradayChart';
// Service imports:
import { formatNumber, convertToPercentage, getAPItoday } from '../../../services/utils';
import { currencyFractionDigits } from '../../../services/config';
import { useApiLmdData } from '../../../services/apiHooks';
// Type imports:
import { IDefaultProps } from '../../../types/Types';
import {  IApiLmdCurrency } from '../../../types/CurrencyTypes';

export type CurrenciesChartType = 'KIB' | 'USD' | 'EUR' | 'GBP' | 'CAD' | 'JPY' | 'CHF' | 'DKK' | 'SEK' | 'NOK' | 'PLN' | 'XDR';

export interface ICurrencyInputState {
    value: number | null,
    currency: string | null,
    averagerate: number | null
}

interface IOwnProps {
    currencySymbol: CurrenciesChartType
}
type Props = IDefaultProps & IOwnProps & React.HTMLAttributes<HTMLDivElement>;

const CurrencyIntradayChart: React.FC<Props> = ({
    accessToken,
    refreshRateMs,
    currencySymbol,
    ...props
}) => {
    const TODAY = getAPItoday();
    const [{data, error}] = useApiLmdData<IApiLmdCurrency[]>(
        `/v1/market_data/v1/currency_cross/base_currency/ISB/cross_currency/${currencySymbol}/intraday_timeseries?from_date=${TODAY}&to_date=${TODAY}`,
        accessToken,
        refreshRateMs
    );

    if (error instanceof Error) {
        return (
            <DisplayBox
                className='KCL_currency-intraday-chart'
                {...props}
            > 
                <div className='head__upper'>
                    <Flag name={currencySymbol as FlagNames} />
                    <h4 className='currency-symbol'>{currencySymbol}</h4>
                    <Alert type='error' headText={error.message}/>
                </div>
            </DisplayBox>
        )
    } else if (data === null) {
        return (
            <DisplayBox
                className='KCL_currency-intraday-chart'
                {...props}
            > 
                <div className='head__upper'>
                    <Flag name={currencySymbol as FlagNames} />
                    <h4 className='currency-symbol'>{currencySymbol}</h4>
                    <Loading />
                </div>
            </DisplayBox>
        );
    } else if (data.length === 0) {
        return (
            <DisplayBox
                className='KCL_currency-intraday-chart'
                {...props}
            > 
                <div className='head__upper'>
                    <Flag name={currencySymbol as FlagNames} />
                    <Alert type={'alert'} headText={'Ekki fundust gögn fyrir daginn í dag.'} />
                </div>
            </DisplayBox>
        );
    }

    const changePer = data.length > 0 ? data[data.length - 1].ChangePer : null;

    return (
        <DisplayBox 
            className='KCL_currency-intraday-chart'
            {...props}
        >
            <div className='intraday-chart__head'>
                <div className='head__upper'>
                    <Flag name={currencySymbol as FlagNames} />
                    <h4 className='currency-symbol'>{currencySymbol}</h4>
                    <span className='currency-averageRate'>
                        {data.length > 0
                            ? formatNumber(data[data.length - 1].Averagerate, '-', currencyFractionDigits(currencySymbol))
                            : null
                        }
                    </span>
                    <span className='currency-changePer'>
                        <ColoredNumber
                            hasArrow
                            sign={(changePer === null)
                                ? 'neutral'
                                : (changePer < 0)
                                ? 'neg'
                                : (changePer > 0)
                                ? 'pos'
                                : 'neutral'
                            }
                        >
                            {convertToPercentage(changePer, true, 2)}
                        </ColoredNumber>
                    </span>
                </div>
                <div className='head__down'>
                    <span className='currency-description'>
                        {data.length > 0
                            ? data[0].Description?.replace(' / Almenntgengi Íslandsbanka', '')
                            : ''
                        }
                    </span>
                </div>
            </div>
            <div className="chart-box__body">
                <IntradayChart
                    data={data}
                />
            </div>
        </DisplayBox>
    );
}

export default CurrencyIntradayChart;